import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { searchService } from "@/services/search";
import { toast } from "sonner";

interface SearchResult {
  title: string;
  link: string;
  snippet: string;
}

interface UseRecipeSearchReturn {
  results: SearchResult[];
  isLoading: boolean;
  errorMessage: string | null;
  searchAttempted: boolean;
  handleSearch: (searchQuery: string, selectedIngredients: string[], excludedIngredients: string[]) => Promise<void>;
}

export const useRecipeSearch = (): UseRecipeSearchReturn => {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchAttempted, setSearchAttempted] = useState(false);

  const handleSearch = async (searchQuery: string, selectedIngredients: string[], excludedIngredients: string[]) => {
    if (!searchQuery.trim() && selectedIngredients.length === 0) {
      toast.error("Please enter a search term or select ingredients");
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage(null);
      setResults([]);
      setSearchAttempted(true);
      
      // Combine search query and ingredients
      let queryString = searchQuery.trim();
      
      if (selectedIngredients.length > 0) {
        if (queryString) {
          queryString += " with ";
        }
        queryString += selectedIngredients.join(", ");
      }

      // Add excluded ingredients
      if (excludedIngredients.length > 0) {
        queryString += " without " + excludedIngredients.join(", ");
      }
      
      console.log("Sending search request with query:", queryString);
      
      // Save search query to database
      await searchService.saveSearch(queryString);
      
      // Call edge function to search for recipes
      const { data, error } = await supabase.functions.invoke("search-recipes", {
        body: { searchQuery: queryString }
      });
      
      if (error) {
        console.error("Supabase function error:", error);
        setErrorMessage(`Error from search function: ${error.message}`);
        throw new Error(`Error searching recipes: ${error.message}`);
      }
      
      if (!data) {
        setErrorMessage("No data received from search function");
        throw new Error("No data received from search function");
      }
      
      console.log("Search function response:", data);
      
      if (data.error) {
        setErrorMessage(`API error: ${data.error}`);
        throw new Error(data.error);
      }
      
      if (!data.results) {
        setErrorMessage("Invalid response format: missing results array");
        throw new Error("Invalid response format");
      }
      
      setResults(data.results);
      
      if (data.results.length === 0) {
        setErrorMessage("No recipes found matching your search criteria. Try different terms or ingredients.");
      }
      
    } catch (error) {
      console.error("Error searching for recipes:", error);
      toast.error("Failed to search for recipes. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    results,
    isLoading,
    errorMessage,
    searchAttempted,
    handleSearch
  };
};
